import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../../../context/LanguageContext";
import Navigation from "../../Navigation";
import Footer from "../../Footer";

const WeensyOSPage = () => {
  const { language } = useLanguage();
  const sectionRefs = useRef([]);

  // 翻译
  const translations = {
    backToProjects: {
      en: "Back to Projects",
      zh: "返回项目列表",
      ja: "プロジェクトに戻る",
    },
    title: {
      en: "WeensyOS - Tiny Operating System",
      zh: "WeensyOS - 微型操作系统",
      ja: "WeensyOS - 小型オペレーティングシステム",
    },
    tagline: {
      en: "Process memory isolation, virtual memory, and system calls implementation",
      zh: "进程内存隔离、虚拟内存和系统调用的实现",
      ja: "プロセスメモリ分離、仮想メモリ、システムコールの実装",
    },
    overview: {
      en: "Project Overview",
      zh: "项目概述",
      ja: "プロジェクト概要",
    },
    description: {
      en: "WeensyOS is an educational operating system that demonstrates core OS concepts. In this project, I implemented complete memory isolation for processes, full virtual memory support, and the fork() system call, all running on x86-64 architecture through QEMU emulation.",
      zh: "WeensyOS是一个教育性质的操作系统，展示了核心操作系统概念。在这个项目中，我实现了进程的完整内存隔离、完整的虚拟内存支持和fork()系统调用，所有这些都通过QEMU模拟在x86-64架构上运行。",
      ja: "WeensyOSは教育用オペレーティングシステムであり、コアなOS概念を示しています。このプロジェクトでは、QEMUエミュレーションを通じてx86-64アーキテクチャ上で動作するプロセスの完全なメモリ分離、完全な仮想メモリサポート、fork()システムコールを実装しました。",
    },
    challenge: {
      en: "The Challenge",
      zh: "项目挑战",
      ja: "課題",
    },
    challengeText: {
      en: "Kernel development presents unique challenges: tiny errors in hardware configuration can crash the entire system, and debugging kernel code is complicated by the fact that the kernel itself provides basic system services. This project required a deep understanding of virtual memory, page tables, process isolation, and system calls.",
      zh: "内核开发面临着独特的挑战：硬件配置中的微小错误可能导致整个系统崩溃，而由于内核本身提供基本的系统服务，因此调试内核代码变得复杂。这个项目需要对虚拟内存、页表、进程隔离和系统调用有深入的理解。",
      ja: "カーネル開発には独自の課題があります：ハードウェア構成のわずかなエラーがシステム全体をクラッシュさせる可能性があり、カーネル自体が基本的なシステムサービスを提供するため、カーネルコードのデバッグは複雑です。このプロジェクトでは、仮想メモリ、ページテーブル、プロセス分離、システムコールに関する深い理解が必要でした。",
    },
    implementation: {
      en: "Implementation Steps",
      zh: "实现步骤",
      ja: "実装ステップ",
    },
    step1: {
      en: "Kernel Isolation",
      zh: "内核隔离",
      ja: "カーネル分離",
    },
    step1Text: {
      en: "Implemented memory protection to prevent applications from accessing kernel memory, except for the CGA console page. This was done using page table permissions, marking kernel memory as present and writable, but not user-accessible.",
      zh: "实现了内存保护，防止应用程序访问内核内存，CGA控制台页面除外。这是通过使用页表权限来完成的，将内核内存标记为存在和可写，但不可用户访问。",
      ja: "CGAコンソールページを除いて、アプリケーションがカーネルメモリにアクセスすることを防ぐメモリ保護を実装しました。これは、ページテーブル権限を使用して実現され、カーネルメモリを存在し書き込み可能としてマークしましたが、ユーザーがアクセスできないようにしました。",
    },
    step2: {
      en: "Process Isolation",
      zh: "进程隔离",
      ja: "プロセス分離",
    },
    step2Text: {
      en: "Created independent page tables for each process, ensuring each process could only access its own memory. Implemented proper handling of page table creation, copying mappings from the kernel page table for addresses below the process start boundary.",
      zh: "为每个进程创建独立的页表，确保每个进程只能访问自己的内存。实现了页表创建的正确处理，为进程起始边界以下的地址从内核页表复制映射。",
      ja: "各プロセスに独立したページテーブルを作成し、各プロセスが自身のメモリにのみアクセスできるようにしました。ページテーブル作成の適切な処理を実装し、プロセス開始境界以下のアドレスについてカーネルページテーブルからマッピングをコピーしました。",
    },
    step3: {
      en: "Virtual Page Allocation",
      zh: "虚拟页面分配",
      ja: "仮想ページ割り当て",
    },
    step3Text: {
      en: "Enhanced the system call for page allocation to use any free physical page to satisfy allocation requests, rather than relying on direct physical-to-virtual mapping. This improved memory utilization and flexibility.",
      zh: "增强了页面分配的系统调用，使用任何空闲的物理页面来满足分配请求，而不是依赖于直接的物理到虚拟映射。这改进了内存利用率和灵活性。",
      ja: "ページ割り当てのシステムコールを強化し、直接的な物理-仮想マッピングに依存するのではなく、空き物理ページを使用して割り当て要求を満たすようにしました。これにより、メモリ利用率と柔軟性が向上しました。",
    },
    step4: {
      en: "Overlapping Address Spaces",
      zh: "重叠地址空间",
      ja: "重複アドレス空間",
    },
    step4Text: {
      en: "Modified process stacks to start from the same virtual address, allowing processes to utilize their full heap allocation potential. This demonstrated a key advantage of virtual memory: different processes can use the same virtual addresses for different physical memory.",
      zh: "修改进程堆栈，使其从相同的虚拟地址开始，允许进程充分利用其堆分配潜力。这展示了虚拟内存的关键优势：不同的进程可以为不同的物理内存使用相同的虚拟地址。",
      ja: "プロセススタックを同じ仮想アドレスから開始するように変更し、プロセスが完全なヒープ割り当て能力を活用できるようにしました。これは仮想メモリの主要な利点を示しています：異なるプロセスが異なる物理メモリに対して同じ仮想アドレスを使用できます。",
    },
    step5: {
      en: "Shared Memory & Resource Management",
      zh: "共享内存与资源管理",
      ja: "共有メモリとリソース管理",
    },
    step5Text: {
      en: "Implemented shared read-only memory between processes to save physical memory, and proper memory reclamation when processes exit to prevent memory leaks.",
      zh: "实现了进程间的共享只读内存以节省物理内存，并在进程退出时正确回收内存以防止内存泄漏。",
      ja: "物理メモリを節約するためのプロセス間の共有読み取り専用メモリと、メモリリークを防ぐためのプロセス終了時の適切なメモリ再利用を実装しました。",
    },
    results: {
      en: "Results",
      zh: "项目成果",
      ja: "成果",
    },
    resultsText: {
      en: "Successfully implemented complete and correct memory isolation for WeensyOS processes, virtual memory with proper page allocation, and the fork() system call. The resulting operating system demonstrated the key concepts of memory protection, virtual addressing, and process creation that are fundamental to modern operating systems.",
      zh: "成功实现了WeensyOS进程的完整且正确的内存隔离、具有适当页面分配的虚拟内存以及fork()系统调用。生成的操作系统展示了内存保护、虚拟寻址和进程创建等现代操作系统的基本概念。",
      ja: "WeensyOSプロセスの完全かつ正確なメモリ分離、適切なページ割り当てを備えた仮想メモリ、およびfork()システムコールを成功裏に実装しました。結果として得られたオペレーティングシステムは、現代のオペレーティングシステムの基本となるメモリ保護、仮想アドレス指定、プロセス作成の主要な概念を示しました。",
    },
    conclusion: {
      en: "Conclusion",
      zh: "结论",
      ja: "結論",
    },
    conclusionText: {
      en: "Working with WeensyOS provided valuable insights into the inner workings of operating systems, particularly in memory management and process isolation. The project reinforced the importance of careful hardware configuration and helped develop debugging skills specific to kernel development. These concepts are directly applicable to understanding modern OS kernels like Linux and contribute to a deeper understanding of system architecture.",
      zh: "使用WeensyOS提供了对操作系统内部工作原理的宝贵见解，特别是在内存管理和进程隔离方面。该项目强调了细致硬件配置的重要性，并帮助开发特定于内核开发的调试技能。这些概念可直接应用于理解像Linux这样的现代操作系统内核，并有助于更深入地理解系统架构。",
      ja: "WeensyOSでの作業は、特にメモリ管理とプロセス分離に関して、オペレーティングシステムの内部動作に関する貴重な洞察を提供しました。このプロジェクトは、慎重なハードウェア構成の重要性を強調し、カーネル開発に特化したデバッグスキルの開発に役立ちました。これらの概念は、LinuxなどのモダンなOSカーネルを理解するために直接適用でき、システムアーキテクチャへの理解を深めることに貢献します。",
    },
    courseInfo: {
      en: "Course Information",
      zh: "课程信息",
      ja: "コース情報",
    },
    course: {
      en: "Course:",
      zh: "课程:",
      ja: "コース:",
    },
    instructor: {
      en: "Instructor:",
      zh: "教师:",
      ja: "教授:",
    },
    ta: {
      en: "Teaching Assistant:",
      zh: "助教:",
      ja: "ティーチングアシスタント:",
    },
    memoryMap: {
      en: "WeensyOS Memory Map visualization showing physical and virtual memory",
      zh: "WeensyOS内存映射可视化展示物理内存和虚拟内存",
      ja: "WeensyOSメモリマップ：物理メモリと仮想メモリの可視化",
    },
    acknowledgments: {
      en: "Acknowledgments",
      zh: "致谢",
      ja: "謝辞",
    },
    acknowledgmentsText1: {
      en: "I would like to express my sincere gratitude to Professor Jocelyn Chen for her excellent guidance throughout the Operating Systems course at NYU. This project was completed as part of CSCI-UA.0202-003, which provided valuable hands-on experience with fundamental OS concepts.",
      zh: "我要向纽约大学Jocelyn Chen教授表达诚挚的感谢，感谢她在操作系统课程中的出色指导。这个项目是CSCI-UA.0202-003课程的一部分，为我提供了宝贵的操作系统基本概念实践经验。",
      ja: "NYUの操作システムコースを通じて優れた指導をしてくださったJocelyn Chen教授に心から感謝します。このプロジェクトはCSCI-UA.0202-003の一部として完成し、OSの基本概念に関する貴重な実践経験を提供してくれました。",
    },
    acknowledgmentsText2: {
      en: "Special thanks also to Teaching Assistant Bob Yao for his helpful support during office hours and debugging sessions.",
      zh: "同时也特别感谢助教Bob Yao在办公时间和调试课程中提供的帮助和支持。",
      ja: "また、オフィスアワーやデバッグセッション中に有益なサポートをしてくれたティーチングアシスタントのBob Yaoにも特別な感謝の意を表します。",
    },
    acknowledgmentsText3: {
      en: "The WeensyOS framework serves as an exceptional educational tool for understanding operating system internals in a practical, accessible way.",
      zh: "WeensyOS框架是一个出色的教育工具，以实用、易于理解的方式帮助学习操作系统内部原理。",
      ja: "WeensyOSフレームワークは、オペレーティングシステムの内部を実践的かつアクセスしやすい方法で理解するための優れた教育ツールとして機能しています。",
    },
    sharedMemoryTitle: {
      en: "Shared Read-Only Memory & Memory Management",
      zh: "共享只读内存与内存管理",
      ja: "共有読み取り専用メモリとメモリ管理",
    },
    sharedMemoryDesc1: {
      en: "Multiple processes can share read-only memory regions to save physical memory. This technique increases memory efficiency while maintaining process isolation.",
      zh: "多个进程可以共享只读内存区域以节省物理内存。这种技术在保持进程隔离的同时提高了内存效率。",
      ja: "複数のプロセスが物理メモリを節約するために読み取り専用メモリ領域を共有できます。この技術はプロセス分離を維持しながらメモリ効率を向上させます。",
    },
    sharedMemoryDesc2: {
      en: "The system also properly reclaims memory when processes exit, preventing memory leaks and ensuring efficient resource utilization.",
      zh: "系统在进程退出时也会正确回收内存，防止内存泄漏并确保资源的高效利用。",
      ja: "システムはプロセスが終了する際に適切にメモリを再利用し、メモリリークを防ぎ、効率的なリソース利用を確保します。",
    },
    architecture: {
      en: "Architecture",
      zh: "系统架构",
      ja: "システムアーキテクチャ",
    },
    githubLink: {
      en: "View on GitHub",
      zh: "在GitHub上查看",
      ja: "GitHubで表示",
    },
  };

  // 技术栈数据
  const techStack = [
    { name: "C", category: "language", color: "blue" },
    { name: "x86-64 Assembly", category: "language", color: "blue" },
    { name: "Virtual Memory", category: "concept", color: "purple" },
    { name: "Page Tables", category: "concept", color: "purple" },
    { name: "QEMU", category: "tools", color: "green" },
    { name: "GDB", category: "tools", color: "green" },
    { name: "Process Isolation", category: "concept", color: "purple" },
    { name: "System Calls", category: "concept", color: "purple" },
  ];

  // 按类别获取技术标签的颜色
  const getTagColor = (category) => {
    switch (category) {
      case "language":
        return "bg-blue-100 text-blue-800";
      case "concept":
        return "bg-purple-100 text-purple-800";
      case "tools":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-neutral-50">
      <Navigation activeSection="projects" />
      <div className="pt-24 pb-12 px-6">
        <div className="container mx-auto max-w-5xl">
          <div className="mb-6 flex justify-between items-center">
            <Link
              to="/projects"
              state={{ projectId: 1 }}
              className="text-primary-500 hover:text-primary-600 transition-colors flex items-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                  clipRule="evenodd"
                />
              </svg>
              {translations.backToProjects[language] ||
                translations.backToProjects.en}
            </Link>

            {/* GitHub链接 */}
            <a
              href="https://github.com/markjailenechan/weensy-os-kernel"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="mr-2"
              >
                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
              </svg>
              {translations.githubLink[language] || translations.githubLink.en}
            </a>
          </div>

          <header className="mb-12 text-center">
            <h1 className="text-4xl md:text-5xl font-light mb-4 text-neutral-800">
              {translations.title[language] || translations.title.en}
            </h1>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              {translations.tagline[language] || translations.tagline.en}
            </p>

            {/* 技术栈标签 */}
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              {techStack.map((tech, index) => (
                <span
                  key={index}
                  className={`${getTagColor(
                    tech.category
                  )} px-3 py-1 rounded-full text-sm font-medium`}
                >
                  {tech.name}
                </span>
              ))}
            </div>
          </header>

          {/* 项目概述 */}
          <section
            ref={(el) => (sectionRefs.current[0] = el)}
            className="mb-16"
          >
            <h2 className="text-2xl font-light mb-6 text-neutral-800 border-b border-neutral-200 pb-2">
              {translations.overview[language] || translations.overview.en}
            </h2>
            <p className="text-neutral-700 mb-8 leading-relaxed">
              {translations.description[language] ||
                translations.description.en}
            </p>

            {/* 项目展示图 */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-8">
              <div className="p-2">
                <img
                  src="/assets/projects/WeensyOS/shared_read_only_memory.gif"
                  alt="WeensyOS Memory Map"
                  className="w-full rounded-lg"
                />
              </div>
              <div className="p-4 text-center text-sm text-neutral-500">
                {translations.memoryMap[language] || translations.memoryMap.en}
              </div>
            </div>

            {/* 课程信息 */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
              <h3 className="text-lg font-medium mb-4 text-neutral-800">
                {translations.courseInfo[language] ||
                  translations.courseInfo.en}
              </h3>
              <p className="text-neutral-700 mb-2">
                <span className="font-medium">
                  {translations.course[language] || translations.course.en}
                </span>{" "}
                CSCI-UA.0202-003 Operating Systems (Undergrad)
              </p>
              <p className="text-neutral-700 mb-2">
                <span className="font-medium">
                  {translations.instructor[language] ||
                    translations.instructor.en}
                </span>{" "}
                Jocelyn Chen
              </p>
              <p className="text-neutral-700">
                <span className="font-medium">
                  {translations.ta[language] || translations.ta.en}
                </span>{" "}
                Bob Yao
              </p>
            </div>
          </section>

          {/* 项目挑战 */}
          <section
            ref={(el) => (sectionRefs.current[1] = el)}
            className="mb-16"
          >
            <h2 className="text-2xl font-light mb-6 text-neutral-800 border-b border-neutral-200 pb-2">
              {translations.challenge[language] || translations.challenge.en}
            </h2>
            <p className="text-neutral-700 mb-8 leading-relaxed">
              {translations.challengeText[language] ||
                translations.challengeText.en}
            </p>
          </section>

          {/* 实现步骤 */}
          <section
            ref={(el) => (sectionRefs.current[2] = el)}
            className="mb-16"
          >
            <h2 className="text-2xl font-light mb-6 text-neutral-800 border-b border-neutral-200 pb-2">
              {translations.implementation[language] ||
                translations.implementation.en}
            </h2>

            <div className="space-y-8">
              {/* 步骤1：内核隔离 */}
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="md:flex">
                  <div className="md:w-1/2 p-6 flex flex-col justify-center">
                    <h3 className="text-xl font-medium mb-3 text-neutral-800">
                      1. {translations.step1[language] || translations.step1.en}
                    </h3>
                    <p className="text-neutral-600">
                      {translations.step1Text[language] ||
                        translations.step1Text.en}
                    </p>
                  </div>
                  <div className="md:w-1/2 bg-gray-50 p-4 flex items-center justify-center">
                    <img
                      src="/assets/projects/WeensyOS/kernel_isolation.gif"
                      alt="Kernel Isolation"
                      className="max-w-full rounded-lg"
                    />
                  </div>
                </div>
              </div>

              {/* 步骤2：进程隔离 */}
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="md:flex">
                  <div className="md:w-1/2 p-6 flex flex-col justify-center md:order-2">
                    <h3 className="text-xl font-medium mb-3 text-neutral-800">
                      2. {translations.step2[language] || translations.step2.en}
                    </h3>
                    <p className="text-neutral-600">
                      {translations.step2Text[language] ||
                        translations.step2Text.en}
                    </p>
                  </div>
                  <div className="md:w-1/2 bg-gray-50 p-4 flex items-center justify-center md:order-1">
                    <img
                      src="/assets/projects/WeensyOS/isolated_address_spaces.gif"
                      alt="Isolated Address Spaces"
                      className="max-w-full rounded-lg"
                    />
                  </div>
                </div>
              </div>

              {/* 步骤3：虚拟页面分配 */}
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="md:flex">
                  <div className="md:w-1/2 p-6 flex flex-col justify-center">
                    <h3 className="text-xl font-medium mb-3 text-neutral-800">
                      3. {translations.step3[language] || translations.step3.en}
                    </h3>
                    <p className="text-neutral-600">
                      {translations.step3Text[language] ||
                        translations.step3Text.en}
                    </p>
                  </div>
                  <div className="md:w-1/2 bg-gray-50 p-4 flex items-center justify-center">
                    <img
                      src="/assets/projects/WeensyOS/virtual_page_allocation.gif"
                      alt="Virtual Page Allocation"
                      className="max-w-full rounded-lg"
                    />
                  </div>
                </div>
              </div>

              {/* 步骤4：重叠地址空间 */}
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="md:flex">
                  <div className="md:w-1/2 p-6 flex flex-col justify-center md:order-2">
                    <h3 className="text-xl font-medium mb-3 text-neutral-800">
                      4. {translations.step4[language] || translations.step4.en}
                    </h3>
                    <p className="text-neutral-600">
                      {translations.step4Text[language] ||
                        translations.step4Text.en}
                    </p>
                  </div>
                  <div className="md:w-1/2 bg-gray-50 p-4 flex items-center justify-center md:order-1">
                    <img
                      src="/assets/projects/WeensyOS/overlapping_address_spaces.gif"
                      alt="Overlapping Address Spaces"
                      className="max-w-full rounded-lg"
                    />
                  </div>
                </div>
              </div>

              {/* 步骤5：共享只读内存和释放内存 */}
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="md:flex">
                  <div className="md:w-1/2 p-6 flex flex-col justify-center">
                    <h3 className="text-xl font-medium mb-3 text-neutral-800">
                      5.{" "}
                      {translations.sharedMemoryTitle[language] ||
                        translations.sharedMemoryTitle.en}
                    </h3>
                    <p className="text-neutral-600 mb-3">
                      {translations.sharedMemoryDesc1[language] ||
                        translations.sharedMemoryDesc1.en}
                    </p>
                    <p className="text-neutral-600">
                      {translations.sharedMemoryDesc2[language] ||
                        translations.sharedMemoryDesc2.en}
                    </p>
                  </div>
                  <div className="md:w-1/2 bg-gray-50 p-4 flex items-center justify-center">
                    <div className="grid grid-cols-1 gap-4">
                      <img
                        src="/assets/projects/WeensyOS/shared_read_only_memory.gif"
                        alt="Shared Read-Only Memory"
                        className="max-w-full rounded-lg mb-4"
                      />
                      <img
                        src="/assets/projects/WeensyOS/freeing_memory.gif"
                        alt="Freeing Memory"
                        className="max-w-full rounded-lg"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* 项目成果 */}
          <section
            ref={(el) => (sectionRefs.current[3] = el)}
            className="mb-16"
          >
            <h2 className="text-2xl font-light mb-6 text-neutral-800 border-b border-neutral-200 pb-2">
              {translations.results[language] || translations.results.en}
            </h2>
            <p className="text-neutral-700 mb-8 leading-relaxed">
              {translations.resultsText[language] ||
                translations.resultsText.en}
            </p>
          </section>

          {/* 结论 */}
          <section
            ref={(el) => (sectionRefs.current[4] = el)}
            className="mb-16"
          >
            <h2 className="text-2xl font-light mb-6 text-neutral-800 border-b border-neutral-200 pb-2">
              {translations.conclusion[language] || translations.conclusion.en}
            </h2>
            <p className="text-neutral-700 mb-8 leading-relaxed">
              {translations.conclusionText[language] ||
                translations.conclusionText.en}
            </p>

            {/* 致谢部分 */}
            <div className="bg-neutral-50 rounded-xl p-6 mt-8">
              <h3 className="text-xl font-light mb-4 text-neutral-800">
                {translations.acknowledgments[language] ||
                  translations.acknowledgments.en}
              </h3>
              <p className="text-neutral-600 mb-4">
                {translations.acknowledgmentsText1[language] ||
                  translations.acknowledgmentsText1.en}
              </p>
              <p className="text-neutral-600 mb-4">
                {translations.acknowledgmentsText2[language] ||
                  translations.acknowledgmentsText2.en}
              </p>
              <p className="text-neutral-600">
                {translations.acknowledgmentsText3[language] ||
                  translations.acknowledgmentsText3.en}
              </p>
            </div>
          </section>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default WeensyOSPage;
